/**
 * @graph-node {Page}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 案件（メンズのプロダクトバックログ）の一覧ページ。全社スプシの開発タブを読み、アサインに近い順の区分ごとに案件の札（題名・キー・Backlog の状態・PL）を並べ、見るだけの区分は畳み、ずれ・区切りの形の変化・読めないことを帯で知らせる。キーで開く URL では、その札に印を付けて見える所まで送る
 * @graph-connects useProductBacklog [calls] 案件一覧の取得
 * @graph-connects ProductBacklogAlerts [calls] 上の帯
 * @graph-connects ProductBacklogShelf [calls] 区分
 */
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { useParams } from '@tanstack/react-router';
import { ExternalLink, RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProductBacklogAlerts } from '../components/product-backlog/ProductBacklogAlerts.js';
import { ProductBacklogShelf } from '../components/product-backlog/ProductBacklogShelf.js';
import { useProductBacklog } from '../hooks/useProductBacklog.js';
import { backlogUrl } from '../lib/backlog-url.js';
import { formatReadAt, visibleSections } from '../lib/product-backlog-view.js';

/**
 * 案件の一覧（開発）。focusKey はキーで開いたときの案件のキー。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 案件一覧を、見出し（スプシを読んだ時刻と最新を読み込む）・上の帯・並べる区分・見るだけの区分（畳む）で出し、キーで開いたときはその札に印を付ける
 * @graph-connects useProductBacklog [calls] 案件一覧の取得
 * @graph-connects ProductBacklogAlerts [calls] 上の帯
 * @graph-connects ProductBacklogShelf [calls] 区分
 * @graph-connects visibleSections [calls] 出す区分
 * @graph-connects formatReadAt [calls] 読んだ時刻の書き方
 */
export function ProductBacklogPage({ focusKey = null }: { focusKey?: string | null }) {
  const { t } = useTranslation();
  const { data, isLoading, isError, isFetching, refetch } = useProductBacklog();
  const [openedKinds, setOpenedKinds] = useState<ReadonlySet<string>>(new Set());
  const now = new Date();
  const reload = () => {
    void refetch();
  };

  const sections = data?.sheetStatus === 'ok' ? visibleSections(data.sections) : null;
  const focusShelf =
    sections === null || focusKey === null
      ? null
      : ([...sections.arrange, ...sections.viewOnly].find((s) =>
          s.cards.some((c) => c.key === focusKey),
        ) ?? null);

  useEffect(() => {
    if (focusKey === null || focusShelf === null) return;
    document
      .querySelector(`[data-key="${CSS.escape(focusKey)}"]`)
      ?.scrollIntoView({ block: 'center' });
  }, [focusKey, focusShelf]);

  const toggle = (kind: string) => {
    setOpenedKinds((current) => {
      const next = new Set(current);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });
  };

  return (
    <div className="max-w-[1000px] pb-16">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-bold text-[var(--color-text-primary)]">
          {t('productBacklog.title')}
        </h1>
        <span className="inline-flex items-center gap-1.5 text-xs text-[var(--color-text-muted)]">
          {data !== undefined && (
            <span className="tabular-nums">
              {t('productBacklog.readAt', { time: formatReadAt(data.readAt, now) })}
            </span>
          )}
          <button
            type="button"
            onClick={reload}
            disabled={isFetching}
            title={t('productBacklog.reload')}
            aria-label={t('productBacklog.reload')}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-primary)] disabled:opacity-50"
          >
            <RefreshCw
              className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`}
              aria-hidden="true"
            />
          </button>
        </span>
      </div>

      {isLoading && (
        <p className="py-8 text-center text-sm text-[var(--color-text-muted)]">
          {t('productBacklog.loading')}
        </p>
      )}
      {isError && (
        <div
          role="alert"
          className="flex flex-wrap items-center gap-3 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-2 text-sm text-[var(--color-text-secondary)]"
        >
          <span>{t('productBacklog.loadError')}</span>
          <Button size="sm" variant="secondary" onClick={reload} disabled={isFetching}>
            {t('productBacklog.reload')}
          </Button>
        </div>
      )}

      {data !== undefined && (
        <ProductBacklogAlerts data={data} now={now} onReload={reload} reloading={isFetching} />
      )}

      {data?.sheetStatus === 'ok' && sections !== null && (
        <>
          {focusKey !== null && focusShelf === null && (
            <div className="mb-3 flex flex-wrap items-center gap-3 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-3 py-2 text-sm text-[var(--color-text-secondary)]">
              <span>{t('productBacklog.notInList', { key: focusKey })}</span>
              <a
                href={backlogUrl(focusKey)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 font-medium text-[var(--color-accent-primary)] hover:underline"
              >
                {t('productBacklog.openBacklog')}
                <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
              </a>
            </div>
          )}
          <div className="flex flex-col gap-3">
            {sections.arrange.map((section) => (
              <ProductBacklogShelf
                key={`${section.kind}-${String(section.separatorRowNumber)}`}
                section={section}
                backlogStatus={data.backlogStatus}
                focusKey={focusKey}
                collapsible={false}
                open
              />
            ))}
            {sections.viewOnly.length > 0 && (
              <div
                role="separator"
                className="mx-0.5 mt-2.5 flex items-center gap-2.5 text-xs text-[var(--color-text-muted)] before:h-px before:flex-1 before:bg-[var(--color-border)] after:h-px after:flex-1 after:bg-[var(--color-border)]"
              >
                {t('productBacklog.viewOnlyLine')}
              </div>
            )}
            {sections.viewOnly.map((section) => {
              const id = `${section.kind}-${String(section.separatorRowNumber)}`;
              return (
                <ProductBacklogShelf
                  key={id}
                  section={section}
                  backlogStatus={data.backlogStatus}
                  focusKey={focusKey}
                  collapsible
                  open={openedKinds.has(id) !== (focusShelf === section)}
                  onToggle={() => {
                    toggle(id);
                  }}
                />
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * キーで開く URL（`/backlog/{キー}`）。一覧を出し、そのキーの札に印を付ける。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題管理などから Backlog キーで案件を開く URL で、案件一覧をそのキーの札に印を付けて出す
 * @graph-connects ProductBacklogPage [calls] 案件一覧
 */
export function ProductBacklogKeyPage() {
  const { key } = useParams({ from: '/backlog/$key' });
  return <ProductBacklogPage focusKey={key.trim().toUpperCase()} />;
}
